// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { Loader2, Trash2, X } from 'lucide-react';
import { useState } from 'react';

export interface BulkAction {
  /** Unique key for this action. */
  key: string;
  /** Display label shown in the dropdown. */
  label: string;
  /** Icon component (rendered at 14x14). */
  icon: React.ComponentType<{ className?: string }>;
  /** Whether this action is destructive (renders in red). */
  destructive?: boolean;
  /** Handler called with the selected IDs. Should return a promise. */
  onExecute: (ids: string[]) => Promise<void>;
}

interface BulkActionBarProps {
  /** Number of currently selected items. */
  selectedCount: number;
  /** Array of selected IDs. */
  selectedIds: string[];
  /** Available bulk actions. */
  actions: BulkAction[];
  /** Deselect all items. */
  onDeselectAll: () => void;
}

/**
 * A floating action bar that appears when items are selected.
 * Inspired by WordPress-style bulk actions — select items, pick an action, apply.
 * Designed for extensibility: pass any array of actions (delete, archive, export, etc).
 */
export function BulkActionBar({
  selectedCount,
  selectedIds,
  actions,
  onDeselectAll,
}: BulkActionBarProps) {
  const [executing, setExecuting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  if (selectedCount === 0) return null;

  async function handleExecute(action: BulkAction) {
    if (action.destructive && confirming !== action.key) {
      setConfirming(action.key);
      return;
    }

    setExecuting(action.key);
    setConfirming(null);
    try {
      await action.onExecute(selectedIds);
      onDeselectAll();
    } finally {
      setExecuting(null);
    }
  }

  const isExecuting = executing !== null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5 shadow-sm">
      <span className="text-[14px] font-medium tabular-nums text-foreground">
        {selectedCount} selected
      </span>

      <div className="h-4 w-px bg-border" />

      <div className="flex items-center gap-1.5">
        {actions.map((action) => {
          const isThisExecuting = executing === action.key;
          const isThisConfirming = confirming === action.key;

          if (isThisConfirming) {
            return (
              <div key={action.key} className="flex items-center gap-1.5">
                <span className="text-[13px] text-destructive">
                  Delete {selectedCount}?
                </span>
                <button
                  type="button"
                  onClick={() => handleExecute(action)}
                  disabled={isExecuting}
                  className="rounded-md bg-destructive px-2.5 py-1 text-[13px] font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(null)}
                  disabled={isExecuting}
                  className="rounded-md px-2.5 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Cancel
                </button>
              </div>
            );
          }

          return (
            <button
              key={action.key}
              type="button"
              onClick={() => handleExecute(action)}
              disabled={isExecuting}
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors disabled:opacity-50 ${
                action.destructive
                  ? 'text-destructive hover:bg-destructive/10'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {isThisExecuting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <action.icon className="h-3.5 w-3.5" />
              )}
              {action.label}
            </button>
          );
        })}
      </div>

      <div className="h-4 w-px bg-border" />

      <button
        type="button"
        onClick={onDeselectAll}
        disabled={isExecuting}
        className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
        aria-label="Deselect all"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * Checkbox for individual item selection within a list.
 * Visually minimal — blends into the card when not hovered.
 */
export function SelectionCheckbox({
  checked,
  onChange,
  visible,
  ariaLabel,
}: {
  checked: boolean;
  onChange: () => void;
  /** Whether to show the checkbox (true when in selection mode or hovered). */
  visible: boolean;
  ariaLabel?: string;
}) {
  return (
    <label
      className={`flex shrink-0 items-center transition-opacity ${
        visible || checked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-foreground"
        aria-label={ariaLabel}
      />
    </label>
  );
}
